import { memo, useMemo } from 'react';
import { Button, Dropdown, Option, Switch, Tooltip } from '@coreai/fluentui-react';
import { ArrowResetRegular } from '@fluentui/react-icons';
import { useAppStore, ASSISTANT_MODEL_AUTOMATIC } from '@/stores/appStore';
import { useModelCatalog } from '@/components/shared/ModelSelector';
import { useSettingCardStyles } from './sectionStyles';

const AUTOMATIC_LABEL = 'Automatic (recommended)';

/**
 * Copilot chat defaults.
 *
 * - `assistantDefaultModel` is forwarded to the assistant backend on every
 *   chat turn (`ChatRequest.Model`). The options are sourced from the live
 *   `listModels` catalog (`useModelCatalog`) so we only ever offer models the
 *   backend actually serves; "Automatic" (empty string) defers to the
 *   backend's own default. Individual sessions can override via the model
 *   picker in the chat composer.
 * - `assistantDefaultAgentMode` seeds the chat store's `agentMode` field on
 *   boot. The in-chat bot/sparkle toggle still wins per session.
 *
 * Storage is local-only — no per-account sync today.
 */
export const AiAssistantSection = memo(() => {
  const styles = useSettingCardStyles();
  // Subscribe per-field — see AppearanceSection for rationale.
  const assistantDefaultModel = useAppStore((s) => s.assistantDefaultModel);
  const setAssistantDefaultModel = useAppStore((s) => s.setAssistantDefaultModel);
  const assistantDefaultAgentMode = useAppStore((s) => s.assistantDefaultAgentMode);
  const setAssistantDefaultAgentMode = useAppStore((s) => s.setAssistantDefaultAgentMode);

  const { models, isGitHubAuthenticated } = useModelCatalog();

  const modelOverridden = assistantDefaultModel !== ASSISTANT_MODEL_AUTOMATIC;

  // Label shown in the closed dropdown. Falls back to the raw id when a
  // persisted pick isn't (yet) in the catalog so the control never renders
  // blank.
  const selectedLabel = useMemo(() => {
    if (!modelOverridden) return AUTOMATIC_LABEL;
    return models.find((m) => m.id === assistantDefaultModel)?.displayName ?? assistantDefaultModel;
  }, [modelOverridden, models, assistantDefaultModel]);

  // Keep a persisted-but-missing pick visible as its own option so the user
  // can see (and clear) a model the catalog no longer offers.
  const showOrphanOption =
    modelOverridden && !models.some((m) => m.id === assistantDefaultModel);

  const helpText = isGitHubAuthenticated
    ? 'Used by Copilot in new chat sessions. Individual sessions can still override this from the chat composer.'
    : 'Used by Copilot in new chat sessions. Sign in to GitHub in the chat to choose from the full model catalog.';

  return (
    <div className={styles.list}>
      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Default model</p>
          <p className={styles.cardHelp}>{helpText}</p>
        </div>
        <div className={styles.cardControl}>
          {modelOverridden && (
            <Tooltip content="Reset to default" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<ArrowResetRegular />}
                onClick={() => setAssistantDefaultModel(ASSISTANT_MODEL_AUTOMATIC)}
                aria-label="Reset default model"
                className={styles.resetBtn}
              />
            </Tooltip>
          )}
          <Dropdown
            aria-label="Default model"
            value={selectedLabel}
            selectedOptions={[assistantDefaultModel]}
            inlinePopup
            positioning="below-start"
            onOptionSelect={(_, data) => {
              if (data.optionValue !== undefined) {
                setAssistantDefaultModel(data.optionValue);
              }
            }}
          >
            <Option value={ASSISTANT_MODEL_AUTOMATIC} text={AUTOMATIC_LABEL}>
              {AUTOMATIC_LABEL}
            </Option>
            {models.map((m) => (
              <Option key={m.id} value={m.id} text={m.displayName}>
                {m.displayName}
              </Option>
            ))}
            {showOrphanOption && (
              <Option value={assistantDefaultModel} text={assistantDefaultModel}>
                {`${assistantDefaultModel} (unavailable)`}
              </Option>
            )}
          </Dropdown>
        </div>
      </div>

      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Start new chats in agent mode</p>
          <p className={styles.cardHelp}>
            When on, new Copilot chats create agentic workflows (agent triggers + agent actions). When off, they
            create traditional workflows. The bot toggle in the chat composer still wins for the current session.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Switch
            checked={assistantDefaultAgentMode}
            onChange={(_, data) => setAssistantDefaultAgentMode(data.checked)}
            aria-label="Start new chats in agent mode"
          />
        </div>
      </div>
    </div>
  );
});
AiAssistantSection.displayName = 'AiAssistantSection';
